import React, { useEffect, useState } from 'react';
import { Alert, alpha, Box, Button, CircularProgress, GlobalStyles, Stack, TextField, Typography } from '@mui/material';
import { apiGet } from 'api/service';
import { GREEN, MONO, SEV_COLOR } from './scanUi';
import ScopeGrades from './ScopeGrades';
import { GRADE_COLOR, comparisonRows, gradeImproved, gradeNote, verdict } from './executiveLogic';

const BRAND_KEY = 'fuzzscan_exec_brand'; // same saved name and logo as the Reporting page
const loadBrand = () => { try { return JSON.parse(localStorage.getItem(BRAND_KEY)) || {}; } catch { return {}; } };
const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];

// Printing shows only the summary, not the app around it.
const printStyles = (
  <GlobalStyles styles={{
    '@media print': {
      'body *': { visibility: 'hidden' },
      '.exec-print, .exec-print *': { visibility: 'visible' },
      '.exec-print': { position: 'absolute', left: 0, top: 0, width: '100%' },
      '.no-print': { display: 'none !important' }
    }
  }} />
);

const Kpi = ({ label, value, color, note }) => (
  <Box sx={{ ...panel, p: 2.2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>{label}</Typography>
    <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO, color, lineHeight: 1.2 }}>{value}</Typography>
    <Typography variant="caption" color="text.secondary">{note}</Typography>
  </Box>
);

export default function ExecutiveSummaryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [brand, setBrand] = useState(loadBrand);

  useEffect(() => {
    apiGet('/api/executive/summary/').then(setData).catch(() => setError('Could not load the summary data.')).finally(() => setLoading(false));
  }, []);

  const updateBrand = (patch) => {
    const next = { ...brand, ...patch };
    setBrand(next);
    try { localStorage.setItem(BRAND_KEY, JSON.stringify(next)); } catch { /* logo too large to save */ }
  };
  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) return setError('The logo must be under 500 KB.');
    const reader = new FileReader();
    reader.onload = () => { setError(''); updateBrand({ logo: reader.result }); };
    reader.readAsDataURL(file);
  };

  const v = data && verdict(data);
  const grade = data?.now.grade;
  const rows = data ? comparisonRows(data) : [];
  const maxSev = data ? Math.max(1, ...SEVERITIES.map((s) => data.now.by_severity[s])) : 1;
  const cur = data?.current_window;
  const prev = data?.previous_window;
  const date = new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      {printStyles}
      <Stack className="no-print" direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Executive summary</Typography>
          <Typography color="text.secondary">One page your board or client can read in two minutes</Typography>
        </Box>
        <Stack direction="row" gap={1.2} alignItems="center" flexWrap="wrap">
          <TextField size="small" placeholder="Company name" value={brand.name || ''} onChange={(e) => updateBrand({ name: e.target.value })} sx={{ minWidth: 200 }} />
          <Button component="label" size="small" variant="outlined" color="inherit">{brand.logo ? 'Change logo' : 'Upload logo'}<input hidden type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={onLogo} /></Button>
          {brand.logo && <Button size="small" color="inherit" onClick={() => updateBrand({ logo: '' })}>Remove</Button>}
          <Button variant="contained" disabled={!data} onClick={() => window.print()} sx={{ height: 44, fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>Save as PDF</Button>
        </Stack>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: GREEN }} /></Box>}

      {data && (
        <Box className="exec-print">
          {(brand.logo || brand.name) && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ borderBottom: 2, borderColor: GREEN, pb: 2, mb: 3 }}>
              <Stack direction="row" alignItems="center" gap={2}>
                {brand.logo && <Box component="img" src={brand.logo} alt="" sx={{ maxHeight: 48, maxWidth: 180, objectFit: 'contain' }} />}
                {brand.name && <Typography variant="h6" fontWeight={700}>{brand.name}</Typography>}
              </Stack>
              <Typography variant="body2" color="text.secondary">Executive summary · {date}</Typography>
            </Stack>
          )}

          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'stretch', mb: 2.5 }}>
            <Box sx={{ flex: '2 1 560px', borderRadius: 3, border: 1, borderColor: alpha(GRADE_COLOR[grade], 0.5), p: { xs: 2.5, md: 4 }, display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap',
              background: `linear-gradient(135deg, ${alpha(GRADE_COLOR[grade], 0.12)}, ${alpha(GRADE_COLOR[grade], 0.02)})` }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 72, fontWeight: 800, fontFamily: MONO, lineHeight: 1, color: GRADE_COLOR[grade] }}>{grade}</Typography>
                <Typography variant="body2" color="text.secondary">Security grade</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: gradeImproved(data) ? GREEN : data.now.score < data.previous.score ? '#ef4444' : 'text.secondary' }}>
                  {gradeImproved(data) ? '▲ ' : data.now.score < data.previous.score ? '▼ ' : ''}{gradeNote(data)}
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Typography variant="overline" sx={{ fontWeight: 700, color: GRADE_COLOR[grade] }}>Verdict</Typography>
                <Typography sx={{ fontSize: 23, fontWeight: 700, lineHeight: 1.35 }}>{v.head}</Typography>
                <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>{v.detail}</Typography>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 320px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <Kpi label="Open critical and high" value={data.now.critical_high} color={data.now.critical_high ? '#ef4444' : GREEN} note={`was ${data.previous.critical_high} 30 days ago`} />
              <Kpi label="Fixed in 30 days" value={cur.fixed} color={GREEN} note={`${cur.new} new appeared`} />
              <Kpi label="Average time to fix" value={cur.avg_days_to_fix == null ? '-' : `${cur.avg_days_to_fix} d`} color="text.primary" note={prev.avg_days_to_fix == null ? 'no earlier figure' : `was ${prev.avg_days_to_fix} d`} />
              <Kpi label="Overdue fixes" value={cur.overdue_cases} color={cur.overdue_cases ? '#f59e0b' : GREEN} note="past their deadline" />
            </Box>
          </Box>

          {data.by_scope && Object.keys(data.by_scope).length > 0 && <Box sx={{ mb: 2.5 }}><ScopeGrades scopes={data.by_scope} /></Box>}

          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'flex-start', mb: 2.5 }}>
            <Box sx={{ ...panel, flex: '1.4 1 520px', minWidth: 0, overflow: 'hidden' }}>
              <Typography fontWeight={600} sx={{ p: 2.5 }}>{data.decisions.length ? `${data.decisions.length === 1 ? 'The one thing' : `The ${data.decisions.length} things`} that need a decision` : 'Nothing needs a decision right now'}</Typography>
              {data.decisions.map((d, i) => (
                <Stack key={d.id} direction="row" gap={2} sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', flex: 'none', display: 'grid', placeItems: 'center', fontWeight: 800, bgcolor: alpha(GREEN, 0.14), color: GREEN }}>{i + 1}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={600}>{d.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: MONO, my: 0.3 }}>{d.address}</Typography>
                    {d.fix ? <Typography variant="body2" sx={{ lineHeight: 1.6 }}><b>Fix:</b> {d.fix}</Typography> : d.summary && <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{d.summary}</Typography>}
                  </Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, px: 1.2, py: 0.4, borderRadius: 1.5, alignSelf: 'flex-start', bgcolor: alpha(SEV_COLOR[d.severity], 0.16), color: SEV_COLOR[d.severity] }}>{d.severity.toUpperCase()}</Typography>
                </Stack>
              ))}
            </Box>
            <Box sx={{ ...panel, flex: '1 1 340px', p: 2.5 }}>
              <Typography fontWeight={600} sx={{ mb: 1.5 }}>Open problems by severity</Typography>
              {SEVERITIES.map((s) => (
                <Stack key={s} direction="row" alignItems="center" gap={1.2} sx={{ mb: 1.1 }}>
                  <Typography variant="body2" sx={{ width: 64, fontWeight: 600, color: SEV_COLOR[s], textTransform: 'capitalize' }}>{s}</Typography>
                  <Box sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: alpha('#fff', 0.08) }}><Box sx={{ height: '100%', width: `${(data.now.by_severity[s] / maxSev) * 100}%`, borderRadius: 1, bgcolor: SEV_COLOR[s] }} /></Box>
                  <Typography sx={{ width: 34, textAlign: 'right', fontFamily: MONO, fontSize: 13 }}>{data.now.by_severity[s]}</Typography>
                </Stack>
              ))}
            </Box>
          </Box>

          <Box sx={{ ...panel, overflow: 'hidden' }}>
            <Typography fontWeight={600} sx={{ p: 2.5 }}>Compared with the 30 days before</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 1.5, px: 2.5, py: 1.2, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, borderTop: 1, borderColor: 'divider' }}>
              <span>Measure</span><span>Before</span><span>Now</span><span>Change</span>
            </Box>
            {rows.map((r) => (
              <Box key={r.label} sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 1.5, px: 2.5, py: 1.6, borderTop: 1, borderColor: 'divider', alignItems: 'center' }}>
                <Typography fontWeight={600}>{r.label}</Typography>
                <Typography sx={{ fontFamily: MONO, color: 'text.secondary' }}>{r.before}</Typography>
                <Typography sx={{ fontFamily: MONO, fontWeight: 700 }}>{r.now}</Typography>
                <Typography sx={{ fontWeight: 700, color: r.good == null ? 'text.secondary' : r.good ? GREEN : '#ef4444' }}>{r.text}</Typography>
              </Box>
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2.5, py: 1.5 }}>
              Open counts are taken at the end of each 30-day period. Fixed, new, time to fix and overdue are measured inside the period.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
